import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCheck } from 'lucide-react';
import { cn } from '../../lib/utils';
import { NotificationRow } from './NotificationRow';
import {
  NOTIF_CATEGORIES,
  useNotificationCenter,
  type NotifItem,
} from './notificationCenterStore';

/**
 * Full activity screen at `/activity` (DESIGN: "C · Full screen") — a dedicated
 * triage inbox + activity log. Type filters, date-grouped rows, and inline
 * actions, all sharing state with the header bell dropdown. The app bar is
 * provided by AppLayout (eyebrow "Activity" + title "Notifications").
 */
export function NotificationCenter() {
  const navigate = useNavigate();
  const { items, unread, markAll, read } = useNotificationCenter();
  const [tab, setTab] = useState<string>('all');

  const shown =
    tab === 'all'
      ? items
      : tab === 'unread'
        ? items.filter((n) => n.unread)
        : items.filter((n) => n.category === tab);

  // bucket into ordered date groups
  const groups: { key: string; items: NotifItem[] }[] = [];
  shown.forEach((n) => {
    let g = groups.find((x) => x.key === n.group);
    if (!g) {
      g = { key: n.group, items: [] };
      groups.push(g);
    }
    g.items.push(n);
  });

  const activate = (item: NotifItem) => {
    read(item.id);
    navigate(item.to);
  };

  return (
    <div className="px-6 py-6">
      <div className="w-full max-w-[780px]">
        {/* toolbar: filter chips + mark all read */}
        <div className="mb-[18px] flex flex-wrap items-center gap-3">
          <div className="inline-flex flex-wrap gap-1 rounded-pill bg-surface-2 p-1">
            {NOTIF_CATEGORIES.map((c) => {
              const active = tab === c.id;
              const showCount = c.id === 'unread' && unread > 0;
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setTab(c.id)}
                  className={cn(
                    'h-[34px] rounded-pill px-[15px] text-[13.5px] transition-colors duration-150',
                    active
                      ? 'bg-background font-semibold text-ink shadow-latio-sm'
                      : 'font-medium text-ink-2 hover:text-ink',
                  )}
                >
                  {c.label}
                  {showCount ? ` · ${unread}` : ''}
                </button>
              );
            })}
          </div>
          <span className="flex-1" />
          <button
            type="button"
            onClick={markAll}
            className="inline-flex h-9 items-center gap-2 rounded-pill border border-hairline px-4 text-[13.5px] font-semibold text-ink transition-colors duration-150 hover:bg-surface-2"
          >
            <CheckCheck className="size-4" />
            Mark all read
          </button>
        </div>

        {/* grouped list */}
        <div className="overflow-hidden rounded-card border border-hairline bg-background">
          {shown.length === 0 ? (
            <div className="py-16 text-center text-[14px] text-ink-3">You’re all caught up.</div>
          ) : (
            groups.map((g, gi) => (
              <div key={g.key}>
                <div
                  className={cn(
                    'bg-surface-2 px-5 pb-1.5 pt-3 text-[11px] font-semibold uppercase tracking-[0.06em] text-ink-3',
                    gi > 0 && 'border-t border-hairline',
                  )}
                >
                  {g.key}
                </div>
                {g.items.map((item, i) => (
                  <NotificationRow
                    key={item.id}
                    item={item}
                    comfortable
                    last={i === g.items.length - 1}
                    onActivate={() => activate(item)}
                  />
                ))}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

export default NotificationCenter;
